import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import mermaid from 'mermaid'
import { Maximize2, ZoomIn, ZoomOut, X, Shrink } from 'lucide-react'

let initialised = false

function ensureInitialised(dark: boolean) {
  const themeVars = dark
    ? {
        primaryColor: '#34a1db',
        primaryTextColor: '#f0f2f8',
        primaryBorderColor: '#34a1db',
        lineColor: '#a0aec0',
        secondaryColor: '#1a3a50',
        tertiaryColor: '#1a1d2e',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        fontSize: '14px',
      }
    : {
        primaryColor: '#34a1db',
        primaryTextColor: '#121333',
        primaryBorderColor: '#34a1db',
        lineColor: '#4a5568',
        secondaryColor: '#e8f5fc',
        tertiaryColor: '#f8f9fb',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        fontSize: '14px',
      }

  if (!initialised) {
    mermaid.initialize({ startOnLoad: false, theme: dark ? 'dark' : 'base', themeVariables: themeVars })
    initialised = true
  } else {
    mermaid.initialize({ startOnLoad: false, theme: dark ? 'dark' : 'base', themeVariables: themeVars })
  }
}

interface MermaidDiagramProps {
  diagram: string
  dark?: boolean
}

let diagramCounter = 0

export function MermaidDiagram({ diagram, dark = false }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [svgContent, setSvgContent] = useState<string>('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [zoom, setZoom] = useState(1)
  const idRef = useRef(`mermaid-${++diagramCounter}`)

  useEffect(() => {
    let cancelled = false

    async function render() {
      setLoading(true)
      setError(null)
      try {
        ensureInitialised(dark)
        const id = `${idRef.current}-${Date.now()}`
        const { svg } = await mermaid.render(id, diagram)
        if (!cancelled) {
          setSvgContent(svg)
          if (containerRef.current) {
            containerRef.current.innerHTML = svg
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to render diagram')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    render()
    return () => { cancelled = true }
  }, [diagram, dark])

  const openExpanded = () => {
    setZoom(1)
    setIsExpanded(true)
  }

  const closeExpanded = () => setIsExpanded(false)

  const zoomIn = () => setZoom(z => Math.min(+(z + 0.25).toFixed(2), 4))
  const zoomOut = () => setZoom(z => Math.max(+(z - 0.25).toFixed(2), 0.25))
  const fitScreen = () => setZoom(1)

  if (error) {
    return (
      <pre className="text-xs text-[var(--text-muted)] bg-[var(--bg-tertiary)] p-4 rounded-lg overflow-x-auto">
        {diagram}
      </pre>
    )
  }

  return (
    <>
      {/* Inline view */}
      <div className="relative group">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 rounded-full border-2 border-[var(--accent-blue)] border-t-transparent animate-spin" />
          </div>
        )}
        <div
          ref={containerRef}
          className={loading ? 'hidden' : 'overflow-x-auto'}
        />
        {!loading && svgContent && (
          <button
            onClick={openExpanded}
            title="Expand diagram"
            className="absolute top-2 right-2 p-1.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)] text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity hover:text-[var(--accent-blue)] hover:border-[var(--accent-blue)]"
          >
            <Maximize2 size={14} />
          </button>
        )}
      </div>

      {/* Expanded modal */}
      {isExpanded && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeExpanded() }}
        >
          <div
            className="flex flex-col rounded-xl shadow-2xl overflow-hidden"
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              width: '90vw',
              maxWidth: '1100px',
              maxHeight: '90vh',
            }}
          >
            {/* Toolbar */}
            <div
              className="flex items-center justify-between px-4 py-2.5 border-b shrink-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-1">
                <button
                  onClick={zoomOut}
                  title="Zoom out"
                  className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <ZoomOut size={16} />
                </button>
                <span className="text-xs font-medium text-[var(--text-muted)] tabular-nums w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  title="Zoom in"
                  className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <ZoomIn size={16} />
                </button>
                <div className="w-px h-4 bg-[var(--border)] mx-1" />
                <button
                  onClick={fitScreen}
                  title="Fit to screen"
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--accent-blue)] transition-colors"
                >
                  <Shrink size={14} />
                  Fit
                </button>
              </div>
              <button
                onClick={closeExpanded}
                title="Close"
                className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Diagram canvas */}
            <div className="overflow-auto flex-1 p-6">
              <div
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                  transition: 'transform 0.15s ease',
                  display: 'inline-block',
                  minWidth: '100%',
                }}
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
